import mapboxgl from 'mapbox-gl'
import store from '/@/store'
import { getRoot } from '/@/root'
import { ELocalStorageKey, EDeviceTypeName } from '/@/types'
import { getDeviceBySn } from '/@/api/manage'
import { message } from 'ant-design-vue'
import dockIcon from '/@/assets/icons/dock.png'
import rcIcon from '/@/assets/icons/rc.png'
import droneIcon from '/@/assets/icons/drone.png'
import { OnlineDevice } from '/@/types/device'

// Module-level map: sn → { device, isDock }
// Persists across calls, populated by registerDeviceMeta (called from tsa.vue)
const deviceMetaMap = new Map<string, { device: OnlineDevice; isDock: boolean }>()

export function registerDeviceMeta (sn: string, device: OnlineDevice, isDock: boolean) {
  deviceMetaMap.set(sn, { device, isDock })
}

export function getDeviceMeta (sn: string) {
  return deviceMetaMap.get(sn)
}

// GPS jitter filter: only move a marker if it has traveled at least this many meters
const MIN_MOVE_METERS = 0.8
const lastMarkerPos = new Map<string, [number, number]>()

function geoDistM (lng1: number, lat1: number, lng2: number, lat2: number): number {
  const R = 6371000
  const r = Math.PI / 180
  const dLat = (lat2 - lat1) * r
  const dLng = (lng2 - lng1) * r
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

export function deviceTsaUpdate () {
  const root = getRoot()

  const icons = new Map([
    [EDeviceTypeName.Aircraft, droneIcon],
    [EDeviceTypeName.Gateway, rcIcon],
    [EDeviceTypeName.Dock, dockIcon],
  ])

  // Read dynamically so RESET_WORKSPACE_STATE replacing markerInfo doesn't stale this reference
  function getMarkers () { return store.state.markerInfo.coverMap }

  function createMarkerElement (type: number): HTMLElement {
    const wrapper = document.createElement('div')
    wrapper.style.cssText = 'width:40px;height:40px;cursor:pointer;'
    const img = document.createElement('img') as HTMLImageElement
    img.src = icons.get(type) || droneIcon
    img.style.cssText = 'width:100%;height:100%;display:block;transform-origin:center center;'
    wrapper.appendChild(img)
    return wrapper
  }

  function initMarker (type: number, name: string, sn: string, lng?: number, lat?: number) {
    const markers = getMarkers()
    if (markers[sn]) return
    const map: mapboxgl.Map = root.$map
    if (!map) return

    const el = createMarkerElement(type)

    // Click on marker → open OSD panel (same as clicking in the left sidebar)
    el.addEventListener('click', () => {
      const meta = deviceMetaMap.get(sn)
      if (!meta) return
      const { device, isDock } = meta
      const current = store.state.osdVisible
      // Toggle if same device, otherwise switch
      if (current.sn === device.sn && current.visible) {
        store.commit('SET_OSD_VISIBLE_INFO', { ...current, visible: false })
      } else {
        store.commit('SET_OSD_VISIBLE_INFO', {
          sn: device.sn,
          callsign: device.callsign,
          model: device.model,
          visible: true,
          gateway_sn: device.gateway.sn,
          is_dock: isDock,
          gateway_callsign: device.gateway.callsign,
          payloads: device.payload
        })
      }
    })

    const initLng = lng || 2.3488
    const initLat = lat || 48.8534
    const initZ = (map as any).queryTerrainElevation({ lng: initLng, lat: initLat }, { exaggerated: false }) ?? 0
    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([initLng, initLat, initZ])
      .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(name))
      .addTo(map)

    markers[sn] = marker
  }

  function removeMarker (sn: string) {
    const markers = getMarkers()
    if (!markers[sn]) return
    markers[sn].remove()
    delete markers[sn]
  }

  function addMarker (sn: string, lng?: number, lat?: number) {
    getDeviceBySn(localStorage.getItem(ELocalStorageKey.WorkspaceId)!, sn)
      .then(data => {
        if (data.code !== 0) {
          message.error(data.message)
          return
        }
        initMarker(data.data.domain, data.data.nickname, sn, lng, lat)
      })
  }

  function moveTo (sn: string, lng: number, lat: number, absoluteAlt?: number) {
    const markers = getMarkers()
    const marker = markers[sn]
    if (!marker) {
      addMarker(sn, lng, lat)
      lastMarkerPos.set(sn, [lng, lat])
      return
    }
    const last = lastMarkerPos.get(sn)
    const moved = !last || geoDistM(last[0], last[1], lng, lat) >= MIN_MOVE_METERS
    // Position horizontale : filtrée anti-jitter. On garde la 3e composante pour compat.
    if (moved) {
      marker.setLngLat([lng, lat])
      lastMarkerPos.set(sn, [lng, lat])
    }
    // Altitude EXACTE du drone (pas plaqué au sol). Marker.setAltitude est RELATIF au terrain
    // RENDU (donc exagéré) : z final = terrain + altitude. Pour viser une altitude absolue MSL,
    // on soustrait le terrain exagéré → l'icône flotte à l'altitude réelle du drone, quel que
    // soit le relief. Mise à jour à CHAQUE tick (un drone qui monte sur place bouge peu en XY).
    // Sans absoluteAlt (dock/gateway au sol) → plaqué (altitude 0).
    const map = root.$map as any
    if (map && typeof (marker as any).setAltitude === 'function') {
      if (absoluteAlt != null) {
        const terrainEx = map.queryTerrainElevation({ lng, lat }, { exaggerated: true }) ?? 0
        ;(marker as any).setAltitude(absoluteAlt - terrainEx)
      } else {
        ;(marker as any).setAltitude(0)
      }
    }
  }

  function rotateTo (sn: string, headingDeg: number) {
    const marker = getMarkers()[sn]
    if (!marker) return
    // Rotate the inner img only — Mapbox owns transform on the wrapper div
    const img = marker.getElement().querySelector('img') as HTMLElement | null
    if (img) img.style.transform = `rotate(${headingDeg}deg)`
  }

  // ── Home-point marker ──────────────────────────────────────────────────────
  let homeMarker: mapboxgl.Marker | null = null

  function initHomeMarker (lng: number, lat: number) {
    const map: mapboxgl.Map = root.$map
    if (!map) return
    if (homeMarker) {
      homeMarker.setLngLat([lng, lat])
      return
    }
    const el = document.createElement('div')
    el.title = 'Point de départ'
    el.style.cssText = [
      'width:28px', 'height:28px', 'border-radius:50%',
      'background:#ff6600', 'border:2px solid white',
      'display:flex', 'align-items:center', 'justify-content:center',
      'color:white', 'font-weight:bold', 'font-size:14px',
      'box-shadow:0 2px 6px rgba(0,0,0,0.5)', 'cursor:default',
    ].join(';')
    el.textContent = 'H'
    homeMarker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([lng, lat])
      .setPopup(new mapboxgl.Popup({ offset: 20 }).setText('Point de départ'))
      .addTo(map)
  }

  function removeHomeMarker () {
    homeMarker?.remove()
    homeMarker = null
  }

  return {
    marker: getMarkers(),
    initMarker,
    removeMarker,
    moveTo,
    rotateTo,
    initHomeMarker,
    removeHomeMarker,
  }
}
